/**
 * The bodies the `clients` module accepts, and the shapes it answers with.
 *
 * `.docs/00-index.md` §5: "Zod schemas for every entity and every API body —
 * `packages/shared/src/schemas/`. API validates and SPA forms resolve with the same
 * object." Session 08's forms take these as their resolver; session 05's actions
 * take the same objects as tool inputs, which is why every field carries
 * `.describe()`.
 *
 * Nothing here transforms: `z.toJSONSchema` has to produce the same shape in both
 * `input` and `output` mode (`.docs/02-shared-core.md`, *For the next session*).
 */
import { z } from 'zod';
import { CURRENCIES } from '../money.js';
import { TIER_CODES } from '../pricing.js';
import { ClientSlugSchema } from './refs.js';
import { Rfc3339Schema } from './primitives.js';
/* -------------------------------------------------------------------- roles */
/**
 * `ARCHITECTURE.md` §Identities and roles, the client role table. The union is
 * declared once, here; `apps/api/src/http/principal.ts` re-exports the type so the
 * API and the SPA cannot drift apart.
 */
export const CLIENT_ROLES = ['owner', 'admin', 'billing', 'member'];
/**
 * What each client role may do — `ARCHITECTURE.md` §Identities and roles, once.
 *
 * | Role | Approve money | Manage seats | See invoices | Pay | Request and comment | Write the site | Publish the site |
 * |---|---|---|---|---|---|---|---|
 * | `owner` | yes | yes | yes | yes | yes | yes | yes |
 * | `admin` | yes | yes | yes | yes | yes | yes | yes |
 * | `billing` | no | no | yes | yes | **no** | no | no |
 * | `member` | no | no | **yes** | no | yes | yes | **no** |
 *
 * It is **here**, beside the roles themselves, because it now has three consumers and
 * not two: the service checks it (`security/permissions.ts`), `modules/auth/access.ts`
 * turns it into Better Auth's access-control roles, and the settings page draws the
 * "what each role may do" table from it. The SPA cannot import `apps/api`, so before
 * this move the third consumer was a hand-copied duplicate that lint could not bind —
 * correct on the day it was written and one permission change away from telling a
 * client something untrue about their own account.
 *
 * The permission names are `.docs/04-auth-clients.md`'s.
 *
 * ## What a column means to a *read* — session 24, S1
 *
 * Until session 24 the table was checked on writes and on nothing else: every `GET
 * /api/clients/me/*` was served to a `billing` seat exactly as to an owner, and
 * `invoices:read` was declared here and asserted nowhere. The audit's question was
 * which of two documents the server should obey, because they disagree:
 *
 *   - **this table**, read as though a role may do only what its row lists, gives a
 *     `billing` seat two permissions and therefore almost no portal at all —
 *     including no dashboard, which is where signing in lands;
 *   - **`.docs/19-client-portal-pages.md` §Edge cases**, which names exactly three
 *     pages a `billing` seat is refused ("requests/projects/files show the
 *     `Forbidden` problem inline as the one line, nav items hidden") and leaves the
 *     rest visible.
 *
 * The resolution, and it is a rule rather than a ruling on one case:
 *
 *   1. **The columns are named capabilities, not an exhaustive read ACL.**
 *      Where a column names a read, the server asks it of the seat. Where no column
 *      names one — the dashboard, the calls page, the monthly reports, the widget
 *      pages, the account, the People card, the notification switches — **every seat
 *      may read it**, and `security/client-reads.test.ts` says so out loud rather
 *      than by omission. Reading the table the other way is what produced a role
 *      locked out of its own landing page, and a rule that does that is wrong
 *      whichever document implies it.
 *   2. **`requests:write` is the column that draws `.docs/19`'s line.** Its roles
 *      are exactly {owner, admin, member} — the one column whose set is "not
 *      `billing`" — so it is what refuses a `billing` seat the requests ledger, the
 *      projects, the files page and its bundle, the delegated-access list, and a
 *      download of anything that is not an invoice or a report. `apps/web`'s
 *      `abilitiesOf` reached the same conclusion for the nav in the same session
 *      (`raisesRequests`), from the same two sentences.
 *   3. **`invoices:read` is the money half**: the invoice ledger, one invoice, its
 *      PDF, the next estimate, the payment method, the prepaid balance and its
 *      ledger.
 *
 * ## The `member` row changed, and it is the row that was wrong
 *
 * This table used to tell a client that a `member` does not see invoices. Nothing
 * has ever implemented that, and session 19 built the opposite on purpose: the
 * projects page — which `.docs/19` grants a `member` — prints, per milestone, the
 * invoice it produced and whether it settled, and the dashboard every seat lands on
 * carries the next invoice's date and estimated total. A row a paying customer reads
 * on their own Settings page has to be true, and the two ways to make this one true
 * were to change the row or to take three pages' worth of facts away from the role
 * the same document hands them to. The row was the mistake, so the row changed.
 *
 * The consequence is stated rather than hidden: **all four roles now hold
 * `invoices:read`**, so the check the server makes on the money reads refuses nobody
 * today. It is still made, from this table and from no copy of it, which is what
 * makes taking the permission away from a role a one-line edit here instead of an
 * audit — and `security/client-reads.test.ts` walks every client read against this
 * object for all four roles, so the edit and the server cannot come apart.
 *
 * ## The two `site:` columns — session 46
 *
 * The blog and the forms are widgets whose backend is the portal itself, and they are
 * the first thing a client *publishes*: a post goes out on their own site under their
 * own name, and a form's recipients decide where their leads are mailed. So the column
 * that gates them is split in two, which no earlier column needed:
 *
 *   - **`site:write`** — owner, admin, member. Drafting and editing posts, uploading
 *     their images, and triaging form submissions (read, handled, spam). The everyday
 *     work, and a `member` seat is the one that most often does it.
 *   - **`site:publish`** — owner, admin. Publishing, unpublishing and scheduling a
 *     post, configuring a form and its recipients, and the site hooks that rebuild the
 *     site: the acts that change what the public sees or where the client's mail goes.
 *
 * `billing` holds neither, for the reason `requests:write` gives — a billing seat is
 * for invoices and payments. **`site:write` names reads too**: the blog workspace (its
 * posts, revisions, authors and images), the forms and their inbox, and the site hooks
 * with their delivery log are all asked it, so a `billing` seat is refused them. That is
 * rule 1 as written — a column that names a read is asked it — and it took no reader
 * away from a page that existed before session 46. `site:publish` names no read.
 */
export const CLIENT_PERMISSIONS = [
    'money:approve',
    'members:manage',
    'invoices:read',
    'invoices:pay',
    'requests:write',
    'site:write',
    'site:publish',
];
/**
 * The matrix. Read down a column to answer "who may do this", across a row to answer
 * "what may this role do". Nothing else in the repository answers either.
 */
export const ROLE_PERMISSIONS = {
    owner: [
        'money:approve',
        'members:manage',
        'invoices:read',
        'invoices:pay',
        'requests:write',
        'site:write',
        'site:publish',
    ],
    admin: [
        'money:approve',
        'members:manage',
        'invoices:read',
        'invoices:pay',
        'requests:write',
        'site:write',
        'site:publish',
    ],
    billing: ['invoices:read', 'invoices:pay'],
    // `invoices:read` and not `invoices:pay`: a member reads what the account is
    // billed — the projects page prints it per milestone — and pays nothing. See the
    // header, "The `member` row changed".
    //
    // `site:write` and not `site:publish` (session 46): a member drafts the posts and
    // triages the leads; what goes out under the client's name is an owner's or an
    // admin's to release. See the header, "The two `site:` columns".
    member: ['invoices:read', 'requests:write', 'site:write'],
};
export const ClientRoleSchema = z
    .enum(CLIENT_ROLES)
    .describe('What this person may do. owner and admin approve money and manage seats; billing sees ' +
    'and pays invoices; member comments and raises requests (ARCHITECTURE.md, Identities ' +
    'and roles).');
/* ----------------------------------------------------------------- the client */
export const MarketTierSchema = z
    .literal(TIER_CODES)
    .describe('The market the work targets, 1 to 3. It sets the price multiplier (PRICING.md) and ' +
    'follows the market, not where the client is incorporated. Staff-only, audited.');
export const BillingCurrencySchema = z
    .enum(CURRENCIES)
    .describe('The one currency this client is invoiced in. Set at onboarding; staff-only.');
export const TimezoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .describe('An IANA time zone, for example America/New_York. Used to render dates in email.');
export const EmailSchema = z
    .email()
    .trim()
    .max(320)
    .describe('An email address. 320 characters is the RFC 5321 maximum for a whole address.');
/**
 * The invoice details a client owns: everything on the *Invoice details* card in
 * `mockups/client/settings.html` and nothing beside it.
 *
 * Strict, not stripping. Tier, currency and the billing day are on the same card and
 * are staff-only; a schema that quietly dropped them would answer 200 to a client who
 * had just tried to change their own tier — accepted-looking, and nothing happened.
 */
export const UpdateClientSchema = z
    .strictObject({
    legalName: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .describe('The legal entity an invoice is made out to, exactly as it should be printed.'),
    billingAddress: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .describe('The postal address printed on the invoice. Free text, newlines kept.'),
    taxId: z
        .string()
        .trim()
        .max(64)
        .describe('The tax number shown on the invoice. Display only — no tax is calculated.'),
    invoiceEmail: EmailSchema.describe('Where invoices are sent. Often finance@, not the owner.'),
    country: z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Z]{2}$/, 'Two uppercase letters, ISO 3166-1 alpha-2.')
        .describe('ISO 3166-1 alpha-2, uppercase — TR, US, DE.'),
    timezone: TimezoneSchema.describe('The company’s own zone. It is what a staff caller’s booking grid and every ' +
        'shared call mail are drawn in — a member’s own zone is clients_set_timezone ' +
        'and is only ever that one person’s.'),
})
    .partial()
    .describe('What a client owner or admin may change about their company: the invoice details ' +
    'and the zone it works in. Every field is optional.');
export const CreateClientSchema = z
    .strictObject({
    slug: ClientSlugSchema,
    name: z.string().trim().min(1).max(200).describe('The company as everyone says it out loud.'),
    marketTier: MarketTierSchema,
    billingCurrency: BillingCurrencySchema,
    timezone: TimezoneSchema.optional(),
    seatLimit: z
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('How many people may hold a seat. Defaults to 5 (.docs/00-index.md §6).'),
    ownerEmail: EmailSchema.describe('The first owner. They get the account and can invite.'),
    ownerName: z.string().trim().min(1).max(200).describe('The first owner’s name.'),
})
    .describe('Everything needed to open an account: the organisation, the client row, one owner.');
/* ---------------------------------------------------------------- membership */
export const InviteMemberSchema = z
    .strictObject({
    email: EmailSchema.describe('Who to invite. One invitation per address per client.'),
    role: ClientRoleSchema,
    /**
     * Optional, and it is the invitee's own name rather than a label for the seat.
     *
     * An invitation creates the `user` row it will later attach a seat to — that is
     * what lets a brand-new address ask for a sign-in link and read its own
     * invitation — and this is the only chance anybody has to say what to call them.
     * `mockups/client/settings.html`'s Invite form asks for an address and a role and
     * nothing else, so it is absent far more often than not; when it is, the People
     * card answers the address, and the person sets their own through
     * `/api/auth/update-user`.
     *
     * It never renames anybody: an address that already has an account keeps the
     * name it already had (`clients/service.ts` `findOrCreateUser`), which is the
     * same rule `updateUserInfoOnLink: false` states for a linked provider.
     */
    name: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .optional()
        .describe('What to call them, if you know. Only used when the address is new here.'),
})
    .describe('Invite one person to a seat. The invitation is good for seven days.');
export const SetMemberRoleSchema = z
    .strictObject({ role: ClientRoleSchema })
    .describe('Change what one member may do. The last owner cannot be demoted.');
export const SetTimezoneSchema = z
    .strictObject({
    /**
     * **`null` means "unset it"**, and the door out of a state the page documents.
     *
     * `GET /api/clients/me/members` answers `timezone: null` for a seat that has
     * never chosen one, and Settings tells that reader "You have not set one, so we
     * use the account's" — a real state, with a sentence of its own. Until session 24
     * (R2-08) this field was a non-empty string, so the moment somebody picked a zone
     * that state became unreachable: 419 options and none of them the one they
     * started in. A control the API cannot express is not a control, so the API
     * expresses it.
     *
     * Nullable rather than optional: the route's body is `{ timezone }` either way,
     * and an *absent* key on a strict object would be a second spelling of "leave it
     * alone" on a write whose whole content is this one field.
     */
    timezone: TimezoneSchema.nullable().describe(`${TimezoneSchema.description ?? ''} null clears it, and the account’s zone stands in again.`),
})
    .describe('A member sets their own zone; it is what their email dates are rendered in.');
/* --------------------------------------------------------------- credentials */
/**
 * `mockups/client/files.html`, *Delegated access*: the four kinds shown are GA4,
 * Search Console, Google Ads and Odoo. `other` is the escape hatch, because the
 * fifth vendor arrives before the migration does.
 */
export const CREDENTIAL_KINDS = ['ga4', 'gsc', 'google_ads', 'odoo', 'other'];
export const CredentialKindSchema = z
    .enum(CREDENTIAL_KINDS)
    .describe('Which account the delegated access is for: ga4, gsc (Search Console), google_ads, odoo, ' +
    'or other. The studio holds delegated access, never ownership.');
export const AddCredentialSchema = z
    .strictObject({
    kind: CredentialKindSchema,
    label: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .describe('What it is, in the client’s words — "Google Analytics 4", "Odoo 19.0".'),
    identifier: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .describe('The account name or property id. Stored in the clear only as a mask — ' +
        'properties/••••••••42 — and in full inside the encrypted secret.'),
    secret: z
        .string()
        .min(1)
        .max(4096)
        .describe('The password, key or token. Encrypted at rest; revealed once, and audited.'),
})
    .describe('Record one delegated credential for a client. Staff only.');
/* ------------------------------------------------------ what the tools answer */
/**
 * A client as every studio tool answers with it.
 *
 * Session 05 added these three (`ClientSchema`, `MemberSchema`, `CredentialSchema`)
 * because an action declares its output and the registry parses a real answer
 * against it on every call the integration suite makes — so a shape that drifts from
 * its handler is a failing test rather than a surprise for the first agent that reads
 * it. `.docs/00-index.md` §5 puts them here so session 20's studio pages render the
 * same object the API validates.
 */
export const ClientSchema = z
    .object({
    slug: ClientSlugSchema,
    name: z.string().describe('The company as everyone says it out loud.'),
    legal_name: z.string().nullable().describe('The legal entity an invoice is made out to.'),
    /*
     * The three below are the account's **billing identity**, and the restricted
     * studio tier does not read them: `clients_list` answers null for all three
     * (`clients/service.ts` `visibleToTier`). Null is therefore 'not set' or 'not
     * yours to read', and a caller that needs the difference is a manager.
     */
    billing_address: z
        .string()
        .nullable()
        .describe('The postal address printed on the invoice. Null at the restricted studio tier, ' +
        'which does not read billing detail.'),
    tax_id: z
        .string()
        .nullable()
        .describe('The tax number shown on the invoice. Display only, and null at the restricted ' +
        'studio tier.'),
    invoice_email: z
        .string()
        .nullable()
        .describe('Where invoices are sent. Often finance@. Null at the restricted studio tier.'),
    country: z.string().nullable().describe('ISO 3166-1 alpha-2, uppercase — TR, US, DE.'),
    market_tier: MarketTierSchema,
    billing_currency: BillingCurrencySchema,
    timezone: TimezoneSchema.describe('The organisation’s IANA zone. A member may hold their own for their email.'),
    seat_limit: z.int().describe('How many people may hold a seat.'),
    archived_at: Rfc3339Schema.nullable().describe('When the account was archived, or null while it is live. An archived account takes ' +
        'on nobody new.'),
    created_at: Rfc3339Schema.describe('When the account was opened.'),
})
    .describe('One client account: who they are, how they are billed, and what they may use.');
export const MemberSchema = z
    .object({
    member_id: z.string().describe('The seat’s id — what clients_set_member_role addresses.'),
    user_id: z.string().describe('The person behind the seat. One person may hold several.'),
    name: z
        .string()
        .describe('Their name, as they gave it — or their address, when nobody ever gave one. ' +
        'hasOwnName tells the two apart.'),
    email: EmailSchema.describe('Their email address. The one they sign in with.'),
    role: ClientRoleSchema,
    timezone: z.string().nullable().describe('Their own zone, for email dates, or null.'),
})
    .describe('One person on a client account.');
/**
 * True when the name we hold for somebody is really theirs, rather than their own
 * address standing in for one.
 *
 * `apps/api/src/modules/clients/queries.ts` `memberViewOf` answers the address when
 * `user.name` is empty, which is most people invited through
 * `mockups/client/settings.html`'s form — it asks for an address and a role. That
 * fallback is deliberate and is better than a blank cell beside an *Edit* button
 * labelled "Edit "; what it is not is two facts. Session 24 (R2-15) found both
 * People tables printing the name over the address, so a nameless seat read
 * `qa-round2@casaturca.comqa-round2@casaturca.com` with no separator.
 *
 * It lives here rather than in either portal because both portals render a person —
 * the client's *People* card and the studio's *Contacts* — and a predicate written
 * twice is two answers to "do we know this person's name" waiting to disagree. It is
 * a fact about the payload, not a rendering rule, which is why it sits beside the
 * schema that publishes the payload.
 */
export function hasOwnName(person) {
    return person.name !== '' && person.name !== person.email;
}
export const InvitationSchema = z
    .object({
    invitation_id: z.string().describe('The invitation’s id. The invitee follows a link to it.'),
    email: EmailSchema.describe('Who was invited.'),
    role: ClientRoleSchema,
    expires_at: Rfc3339Schema.describe('When it stops working. Seven days from the invite.'),
})
    .describe('One pending invitation. It holds a seat until it is accepted or expires.');
export const CredentialSchema = z
    .object({
    credential_id: z
        .string()
        .describe('The credential’s id — what clients_reveal_credential takes.'),
    kind: CredentialKindSchema,
    label: z.string().describe('What it is, in the client’s words.'),
    identifier_masked: z
        .string()
        .describe('The account name or property id, masked — properties/••••••••42.'),
    granted_at: Rfc3339Schema.describe('When the client gave us this access.'),
    revoked_at: Rfc3339Schema.nullable().describe('When it was revoked, or null while it works.'),
})
    .describe('One delegated credential. The studio holds delegated access, never ownership.');
//# sourceMappingURL=clients.js.map